// ProductModal.jsx - Place in components/dashboard/ProductModal.jsx
import React, { useState, useEffect } from 'react';

export const ProductModal = ({ show, onClose, onSubmit, isEdit, currentItem, brands }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        specifications: [], // Array of {key: string, value: string}
        brand_id: '',
        category: '',
        price: '',
        images: [] // Array of File objects
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEdit && currentItem) {
            setFormData({
                title: currentItem.title,
                description: currentItem.description,
                specifications: Object.entries(currentItem.specifications || {}).map(([key, value]) => ({ key, value: String(value) })),
                brand_id: currentItem.brand_id,
                category: currentItem.category,
                price: currentItem.price,
                images: [] // For edit, you might want to display existing images and allow adding more; here resetting to empty for simplicity
            });
        } else {
            setFormData({
                title: '',
                description: '',
                specifications: [],
                brand_id: '',
                category: '',
                price: '',
                images: []
            });
        }
    }, [isEdit, currentItem]);

    const validate = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Required';
        if (!formData.category) newErrors.category = 'Required';
        if (!formData.price || isNaN(formData.price)) newErrors.price = 'Valid price required';
        if (!formData.brand_id) newErrors.brand_id = 'Required';
        if (formData.specifications.length === 0) newErrors.specifications = 'At least one specification required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const formDataToSubmit = new FormData();
            formDataToSubmit.append('title', formData.title);
            formDataToSubmit.append('description', formData.description);
            formDataToSubmit.append('specifications', JSON.stringify(Object.fromEntries(formData.specifications.map(spec => [spec.key, spec.value]))));
            formDataToSubmit.append('brand_id', formData.brand_id);
            formDataToSubmit.append('category', formData.category);
            formDataToSubmit.append('price', formData.price);

            formData.images.forEach((file, index) => {
                formDataToSubmit.append(`images[${index}]`, file);
            });

            // For edit, include product ID if needed
            if (isEdit && currentItem) {
                formDataToSubmit.append('id', currentItem.id);
            }

            onSubmit(formDataToSubmit);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSpecChange = (index, field, value) => {
        const newSpecs = [...formData.specifications];
        newSpecs[index][field] = value;
        setFormData({ ...formData, specifications: newSpecs });
    };

    const addSpecification = () => {
        setFormData({ ...formData, specifications: [...formData.specifications, { key: '', value: '' }] });
    };

    const removeSpecification = (index) => {
        const newSpecs = formData.specifications.filter((_, i) => i !== index);
        setFormData({ ...formData, specifications: newSpecs });
    };

    const handleImageChange = (e) => {
        setFormData({ ...formData, images: Array.from(e.target.files) });
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded max-w-md w-full mx-4 max-h-96 overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">{isEdit ? 'Edit Product' : 'Add Product'}</h2>
                <form onSubmit={handleSubmit}>
                    <input name="title" value={formData.title} onChange={handleChange} placeholder="Title" className="w-full p-2 border mb-2" />
                    {errors.title && <p className="text-red-500 text-sm mb-2">{errors.title}</p>}
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" className="w-full p-2 border mb-2" rows={3} />
                    <select name="brand_id" value={formData.brand_id} onChange={handleChange} className="w-full p-2 border mb-2">
                        <option value="">Select Brand</option>
                        {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    {errors.brand_id && <p className="text-red-500 text-sm mb-2">{errors.brand_id}</p>}
                    <input name="category" value={formData.category} onChange={handleChange} placeholder="Category" className="w-full p-2 border mb-2" />
                    {errors.category && <p className="text-red-500 text-sm mb-2">{errors.category}</p>}
                    <input name="price" type="number" value={formData.price} onChange={handleChange} placeholder="Price" className="w-full p-2 border mb-2" step="0.01" />
                    {errors.price && <p className="text-red-500 text-sm mb-2">{errors.price}</p>}

                    {/* Specifications */}
                    <div className="mb-2">
                        <label className="block text-sm font-medium mb-1">Specifications</label>
                        {formData.specifications.map((spec, index) => (
                            <div key={index} className="flex space-x-2 mb-2">
                                <input
                                    type="text"
                                    placeholder="Key"
                                    value={spec.key}
                                    onChange={(e) => handleSpecChange(index, 'key', e.target.value)}
                                    className="flex-1 p-2 border"
                                />
                                <input
                                    type="text"
                                    placeholder="Value"
                                    value={spec.value}
                                    onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                                    className="flex-1 p-2 border"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeSpecification(index)}
                                    className="px-2 py-1 bg-red-500 text-white rounded"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        {errors.specifications && <p className="text-red-500 text-sm">{errors.specifications}</p>}
                        <button
                            type="button"
                            onClick={addSpecification}
                            className="text-blue-500 underline text-sm"
                        >
                            Add Specification
                        </button>
                    </div>

                    {/* Images */}
                    <div className="mb-2">
                        <label className="block text-sm font-medium mb-1">Images</label>
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full p-2 border"
                        />
                    </div>

                    <div className="flex justify-end space-x-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded">{isEdit ? 'Update' : 'Add'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};